import envConfig from '@hulu/env-config';
import { User } from 'mission-control-common-components';
import { useCallback, useEffect, useRef, useState } from 'react';

import { sendTokenToWorker } from '../../configs/serviceWorkerInit';
import { getCognitoDetailsCached } from '../../utils/cognitoDetails';

/**
 * Represents the state and methods of a fetch operation.
 */
export interface FetchResult<T> {
  data: T | undefined;
  loading: boolean;
  loadingMore: boolean;
  error: string | undefined;
  refetch: (append?: boolean) => Promise<void>;
  reset: () => void;
}

/**
 * Configuration options for the useFetch hook.
 */
export interface UseFetchOptions<T> {
  /** HTTP method (default: 'GET') */
  method?: string;
  /** Request body - will be JSON stringified */
  body?: unknown;
  /** Transform the response data before storing */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  mapResponse?: (data: any) => T;
  /** Callback invoked when an error occurs */
  notifyError?: (message: string) => void;
  /** Whether the fetch is enabled (default: true) */
  enabled?: boolean;
  /** If true, don't fetch automatically on mount (default: false) */
  lazy?: boolean;
  /** Whether to refetch when component mounts (default: true) */
  refetchOnMount?: boolean;
  /** Whether to refetch when browser comes back online (default: false) */
  refetchOnReconnect?: boolean;
  /** Enable data accumulation for pagination (default: false) */
  accumulate?: boolean;
  /** Key of the array property to accumulate (e.g., 'nodes') */
  accumulateKey?: keyof T;
}

interface FetchState<T> {
  data: T | undefined;
  loading: boolean;
  loadingMore: boolean;
  error: string | undefined;
}

const isAbortError = (err: unknown): boolean => err instanceof DOMException && err.name === 'AbortError';

/**
 * Extracts a user-friendly error message from an error object.
 * Handles network errors, TypeError, and other error types gracefully.
 */
const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    // Handle network errors (fetch failures, CORS, etc.)
    if (err.message.includes('fetch')) {
      return 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
    }
    // Return the error message (which should already be user-friendly after our improvements)
    return err.message;
  }
  return 'An unknown error occurred. Please try again.';
};

/**
 * Creates a user-friendly error message for authentication failures.
 * Preserves original error context while providing actionable information.
 */
const createAuthErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();

    // Provide specific messages based on error type
    if (errorMessage.includes('token') && errorMessage.includes('missing')) {
      return 'Authentication token is missing. Please try logging in again.';
    }
    if (errorMessage.includes('token') && (errorMessage.includes('expired') || errorMessage.includes('invalid'))) {
      return 'Your session has expired. Please try logging in again.';
    }
    if (errorMessage.includes('refresh')) {
      return 'Unable to refresh your session. Please try logging in again.';
    }

    // Preserve original error message if it's informative
    return `Authentication failed: ${error.message}`;
  }

  return 'Authentication failed. Please try logging in again.';
};

/**
 * Creates a user-friendly error message for HTTP response errors.
 * Provides specific messages for common HTTP status codes.
 */
const createHttpErrorMessage = (status: number, statusText: string): string => {
  // Add more specific messages for common HTTP status codes
  if (status === 401) {
    return 'Authentication required. Please try logging in again.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return 'The requested resource was not found.';
  }
  if (status === 500) {
    return 'Server error occurred. Please try again later.';
  }
  if (status >= 500) {
    return `Server error (${status}). Please try again later.`;
  }

  // Default message with status text if available
  const defaultMessage = `Request failed with status ${status}`;
  return statusText !== 'Unknown error' ? `${defaultMessage}: ${statusText}` : defaultMessage;
};

/**
 * Validates HTTP response status and throws an error for non-success codes.
 */
const validateResponseStatus = (response: Response): void => {
  if (response.status < 200 || response.status >= 300) {
    const statusText = response.statusText || 'Unknown error';
    throw new Error(createHttpErrorMessage(response.status, statusText));
  }
};

/**
 * Parses JSON response with proper error handling.
 * Throws descriptive errors for invalid JSON or unexpected content types.
 */
const parseJsonResponse = async (response: Response): Promise<unknown> => {
  // 204 No Content explicitly has no body
  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get('content-type');

  // Check content-type before attempting to parse
  if (contentType && !contentType.includes('application/json')) {
    throw new Error(`Expected JSON response but received ${contentType}`);
  }

  const text = await response.text();
  if (!text) {
    // For 201/202, an empty body is acceptable
    if (response.status === 201 || response.status === 202) {
      return undefined;
    }
    throw new Error('Empty response from server');
  }

  try {
    return JSON.parse(text);
  } catch {
    // Log the parse failure for debugging but provide a clean error message
    console.error('Failed to parse JSON response:', text.substring(0, 200));
    throw new Error('Invalid JSON response from server');
  }
};

/**
 * Builds request headers for the fetch call.
 */
const buildRequestHeaders = (method: string, hasBody: boolean): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (method !== 'GET' && method !== 'HEAD' && hasBody) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

/**
 * Handles authentication by validating/refreshing the ID token.
 * Returns the authorization header value if auth is enabled.
 */
const handleAuthentication = async (): Promise<string | null> => {
  try {
    await User.checkIdToken(undefined, await getCognitoDetailsCached());
    sendTokenToWorker(navigator);
    const { idToken } = User.getUserInfo();
    if (!idToken) {
      throw new Error('ID token is missing after validation');
    }
    return `Bearer ${idToken}`;
  } catch (authError) {
    console.error('Authentication error:', authError);
    const authErrorMessage = createAuthErrorMessage(authError);
    const error = new Error(authErrorMessage);
    if (authError instanceof Error) {
      (error as Error & { originalError?: Error }).originalError = authError;
    }
    throw error;
  }
};

/**
 * Accumulates paginated data by merging arrays at the specified key.
 */
const accumulateData = <T>(prevData: T, newData: T, accumulateKey: keyof T): T => {
  const prevArray = prevData[accumulateKey];
  const newArray = newData[accumulateKey];
  if (Array.isArray(prevArray) && Array.isArray(newArray)) {
    return {
      ...newData,
      [accumulateKey]: [...prevArray, ...newArray],
    } as T;
  }
  return newData;
};

/**
 * Custom hook for fetching data with built-in auth, error handling, and lifecycle management.
 * Supports data accumulation for pagination scenarios.
 *
 * @param url - The URL to fetch from
 * @param options - Configuration options
 * @returns FetchResult with data, loading states, error, and control methods
 */
export function useFetch<T>(
  url: string,
  {
    method = 'GET',
    body,
    mapResponse,
    notifyError,
    enabled = true,
    lazy = false,
    refetchOnMount = true,
    refetchOnReconnect = false,
    accumulate = false,
    accumulateKey,
  }: UseFetchOptions<T> = {}
): FetchResult<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: undefined,
    loading: false,
    loadingMore: false,
    error: undefined,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const isAuthEnabled = envConfig.REACT_APP_AUTH_FEATURE_ENABLED === 'true';

  // Stabilize body reference to prevent unnecessary refetches
  const stableBody = useRef(body);
  const bodyString = JSON.stringify(body);
  useEffect(() => {
    stableBody.current = body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyString]);

  // Stabilize callbacks to avoid triggering useCallback dependencies
  const stableMapResponse = useRef(mapResponse);
  const stableNotifyError = useRef(notifyError);
  useEffect(() => {
    stableMapResponse.current = mapResponse;
    stableNotifyError.current = notifyError;
  }, [mapResponse, notifyError]);

  const fetchData = useCallback(
    async (shouldAppend = false): Promise<void> => {
      if (!url || !enabled) return;

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setState((prev) => ({
        ...prev,
        loading: !shouldAppend,
        loadingMore: shouldAppend,
        error: undefined,
      }));

      try {
        const headers = buildRequestHeaders(method, Boolean(stableBody.current));

        if (isAuthEnabled) {
          const authHeader = await handleAuthentication();
          if (authHeader) {
            headers.authorization = authHeader;
          }
        }

        const response = await fetch(url, {
          method,
          headers,
          body: stableBody.current ? JSON.stringify(stableBody.current) : undefined,
          signal: abortControllerRef.current.signal,
        });

        validateResponseStatus(response);

        const result = await parseJsonResponse(response);
        const mappedData: T = stableMapResponse.current ? stableMapResponse.current(result) : (result as T);

        setState((prev) => {
          if (accumulate && shouldAppend && prev.data && accumulateKey) {
            return {
              data: accumulateData(prev.data, mappedData, accumulateKey),
              loading: false,
              loadingMore: false,
              error: undefined,
            };
          }
          return {
            data: mappedData,
            loading: false,
            loadingMore: false,
            error: undefined,
          };
        });
      } catch (err: unknown) {
        if (isAbortError(err)) return;

        const errorMessage = getErrorMessage(err);
        setState((prev) => ({
          // Preserve existing data on pagination errors
          data: shouldAppend ? prev.data : undefined,
          loading: false,
          loadingMore: false,
          error: errorMessage,
        }));
        stableNotifyError.current?.(errorMessage);
      }
    },
    [url, method, enabled, isAuthEnabled, accumulate, accumulateKey]
  );

  const reset = useCallback(() => {
    setState({
      data: undefined,
      loading: false,
      loadingMore: false,
      error: undefined,
    });
  }, []);

  // Initial fetch on mount or when dependencies change
  useEffect(() => {
    if (!lazy && refetchOnMount && enabled) {
      fetchData();
    }
  }, [fetchData, enabled, refetchOnMount, lazy]);

  // Refetch when browser comes back online
  useEffect(() => {
    if (!refetchOnReconnect) return;

    const handleOnline = (): void => {
      if (enabled && !lazy) {
        fetchData();
      }
    };

    window.addEventListener('online', handleOnline);
    return (): void => window.removeEventListener('online', handleOnline);
  }, [fetchData, enabled, refetchOnReconnect, lazy]);

  // Cleanup: abort pending request on unmount
  useEffect(() => {
    return (): void => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    data: state.data,
    loading: state.loading,
    loadingMore: state.loadingMore,
    error: state.error,
    refetch: fetchData,
    reset,
  };
}
